import type { DatabricksColumnsResponse } from '@protoss/shared';
import * as React from 'react';
import { dataCatalogApi } from '../services/dataCatalogApi';

const PAGE_SIZE = 50;

export function useTableColumns(
  projectUuid: string,
  catalogName: string | null,
  schemaName: string | null,
  tableName: string | null
) {
  const [data, setData] = React.useState<DatabricksColumnsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);

  const load = React.useCallback(
    async (targetPage: number) => {
      if (!catalogName || !schemaName || !tableName) return;
      setLoading(true);
      setError(null);
      try {
        const result = await dataCatalogApi.getColumns(
          projectUuid,
          catalogName,
          schemaName,
          tableName,
          targetPage,
          PAGE_SIZE
        );
        setData(result);
        setPage(targetPage);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [projectUuid, catalogName, schemaName, tableName]
  );

  React.useEffect(() => {
    setData(null);
    setPage(1);
    if (catalogName && schemaName && tableName) {
      void load(1);
    }
  }, [projectUuid, catalogName, schemaName, tableName]); // eslint-disable-line react-hooks/exhaustive-deps

  const nextPage = React.useCallback(() => {
    if (!data) return;
    const totalPages = Math.ceil(data.total / PAGE_SIZE);
    if (page < totalPages) void load(page + 1);
  }, [data, page, load]);

  const prevPage = React.useCallback(() => {
    if (page > 1) void load(page - 1);
  }, [page, load]);

  const reload = React.useCallback(() => {
    void load(page);
  }, [load, page]);

  return { data, loading, error, page, pageSize: PAGE_SIZE, nextPage, prevPage, reload };
}
